/**
 * QR Health — admin dashboard for the unified QR token system.
 *
 * Surfaces (a) stock counts, (b) per-day timeline (issues / scans / reds /
 * revocations), (c) top scanners, (d) recent RED-verdict scans (security
 * signal), (e) orphan tokens whose linked records no longer exist.
 *
 * Powered by `GET /api/qr/admin/health` (super-admin only).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ShieldAlert, QrCode, Activity, Users, AlertTriangle, RefreshCcw, CheckCircle2, XCircle } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;

const fetchHealth = async (days) => {
  const { data } = await axios.get(`${API}/api/qr/admin/health`, {
    params: { days },
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  return data;
};

const TotalCard = ({ label, value, accent, icon: Icon }) => (
  <Card data-testid={`qr-total-${label.toLowerCase()}`}>
    <CardContent className="pt-5 pb-4 flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-3xl font-bold mt-1">{value}</p>
      </div>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
    </CardContent>
  </Card>
);

const Timeline = ({ rows }) => {
  if (!rows?.length) return <p className="text-sm text-slate-500">No activity in this window.</p>;
  const maxScans = Math.max(1, ...rows.map(r => r.scans));
  return (
    <div className="space-y-1.5" data-testid="qr-timeline">
      {rows.map((r) => (
        <div key={r.day} className="flex items-center gap-3 text-xs">
          <span className="font-mono w-20 text-slate-500">{r.day.slice(5)}</span>
          <div className="flex-1 bg-slate-100 rounded-full h-2 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-indigo-400"
                 style={{ width: `${(r.scans / maxScans) * 100}%` }} />
            {r.reds > 0 && (
              <div className="absolute inset-y-0 left-0 bg-rose-500"
                   style={{ width: `${(r.reds / maxScans) * 100}%` }} />
            )}
          </div>
          <span className="font-mono text-slate-600 w-12 text-right">{r.scans}</span>
          {r.reds > 0 && <Badge variant="destructive" className="text-[10px]">{r.reds} RED</Badge>}
          {r.issued > 0 && <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">+{r.issued} new</Badge>}
        </div>
      ))}
    </div>
  );
};

const QRHealth = () => {
  const [days, setDays] = React.useState(7);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['qr-health', days],
    queryFn: () => fetchHealth(days),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return <div className="p-8 text-slate-500">Loading QR health…</div>;
  }
  if (!data) {
    return <div className="p-8 text-rose-600">Failed to load QR health data.</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="qr-health-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <QrCode className="h-6 w-6 text-indigo-600" />
            QR Health
          </h1>
          <p className="text-sm text-slate-500">
            Unified QR token registry — stock, scans, and security signals over the last {days} days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32" data-testid="qr-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24h</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} data-testid="qr-refresh-btn">
            <RefreshCcw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TotalCard label="Active" value={data.totals?.active ?? 0} icon={CheckCircle2}
                   accent="bg-emerald-100 text-emerald-700" />
        <TotalCard label="Revoked" value={data.totals?.revoked ?? 0} icon={XCircle}
                   accent="bg-rose-100 text-rose-700" />
        <TotalCard label="Consumed" value={data.totals?.consumed ?? 0} icon={Activity}
                   accent="bg-slate-100 text-slate-600" />
        <TotalCard label="Expired" value={data.totals?.expired ?? 0} icon={AlertTriangle}
                   accent="bg-amber-100 text-amber-700" />
      </div>

      {/* Timeline + by-type */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Daily activity</CardTitle>
          </CardHeader>
          <CardContent>
            <Timeline rows={data.timeline} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Active by type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="qr-by-type">
            {Object.entries(data.by_type || {}).length === 0 && (
              <p className="text-sm text-slate-500">No active tokens yet.</p>
            )}
            {Object.entries(data.by_type || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{k}</span>
                <Badge variant="outline" className="font-mono">{v}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Top scanners + Red alerts */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-500" />
              Top scanners
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="qr-top-scanners">
            {(data.top_scanners || []).length === 0 && (
              <p className="text-sm text-slate-500">No scans recorded yet.</p>
            )}
            {(data.top_scanners || []).map((s) => (
              <div key={s.scanner_user_id} className="flex items-center justify-between text-sm">
                <span className="font-mono text-xs text-slate-600 truncate max-w-[60%]">{s.scanner_user_id}</span>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{s.scans} scans</Badge>
                  {s.reds > 0 && (
                    <Badge variant="destructive">{s.reds} red</Badge>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-rose-500" />
              Recent RED scans
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-72 overflow-y-auto" data-testid="qr-red-alerts">
            {(data.red_alerts || []).length === 0 && (
              <p className="text-sm text-emerald-600">No failed scans — clean 🎉</p>
            )}
            {(data.red_alerts || []).map((r, i) => (
              <div key={i} className="border border-rose-100 bg-rose-50/40 rounded p-2 text-xs">
                <div className="flex items-center justify-between">
                  <Badge variant="destructive" className="text-[10px]">{r.reason}</Badge>
                  <span className="text-slate-500">{(r.scanned_at || '').slice(0, 19).replace('T', ' ')}</span>
                </div>
                <div className="mt-1 text-slate-600">
                  type=<span className="font-mono">{r.token_type || 'unknown'}</span>
                  {r.scanner_user_id && <> · scanner=<span className="font-mono">{r.scanner_user_id.slice(0, 12)}…</span></>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Orphans */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Orphan tokens
            {data.orphans?.count > 0 && (
              <Badge variant="destructive">{data.orphans.count}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-3">{data.orphans?.note}</p>
          {data.orphans?.count === 0 ? (
            <p className="text-sm text-emerald-600">All active tokens resolve to live records.</p>
          ) : (
            <div className="space-y-1" data-testid="qr-orphans">
              {(data.orphans?.sample || []).map((o, i) => (
                <div key={i} className="text-xs flex items-center gap-2">
                  <Badge variant="outline">{o.token_type}</Badge>
                  <span className="font-mono text-slate-500">{o.token}</span>
                  <span className="text-slate-400">→ missing record {o.linked_id}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QRHealth;
